import { useState } from "react";
import { FileOutput, Copy, Check, Type, AlertCircle, Package, ListChecks, MessageSquareQuote, MousePointerClick, Shield, HelpCircle, Image, DollarSign, Zap, RefreshCw, Download, Code, FileText, Pencil, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import type { SalesPageData } from "@/pages/app/SalesPageBuilder";

// ── Helpers ──

function CopyBtn({ text, label = "Copy" }: { text: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground" onClick={() => {
      navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success("Copied!");
      setTimeout(() => setCopied(false), 2000);
    }}>
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      <span className="text-xs">{copied ? "Copied" : label}</span>
    </Button>
  );
}

function EditableText({ value, onSave, multiline = false }: { value: string; onSave: (v: string) => void; multiline?: boolean }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  if (!editing) {
    return (
      <span className="group relative cursor-pointer" onClick={() => { setDraft(value); setEditing(true); }}>
        {value}
        <Pencil className="w-3 h-3 text-muted-foreground opacity-0 group-hover:opacity-100 inline ml-1.5 transition-opacity" />
      </span>
    );
  }

  return (
    <div className="space-y-2">
      {multiline ? (
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} autoFocus />
      ) : (
        <input
          className="w-full bg-background border border-border rounded-md px-3 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          autoFocus
        />
      )}
      <div className="flex gap-2">
        <Button size="sm" variant="default" onClick={() => { onSave(draft); setEditing(false); }}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
      </div>
    </div>
  );
}

function SectionCard({ badge, badgeColor, icon: Icon, children, copyText, sectionKey, onRegenerate }: {
  badge: string;
  badgeColor: string;
  icon: typeof Type;
  children: React.ReactNode;
  copyText?: string;
  sectionKey?: string;
  onRegenerate?: (key: string) => void;
}) {
  const [regen, setRegen] = useState(false);

  const handleRegenerate = async () => {
    if (!sectionKey || !onRegenerate) return;
    setRegen(true);
    await onRegenerate(sectionKey);
    setRegen(false);
  };

  return (
    <div className="rounded-xl border border-border/50 bg-card/60 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-border/30 bg-muted/20">
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${badgeColor}`}>
            <Icon className="w-4 h-4" />
          </div>
          <Badge variant="outline" className="text-[10px] font-bold tracking-wider uppercase border-border/50">
            {badge}
          </Badge>
        </div>
        <div className="flex items-center gap-1">
          {sectionKey && onRegenerate && (
            <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground" onClick={handleRegenerate} disabled={regen}>
              {regen ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="text-xs">Regenerate</span>
            </Button>
          )}
          {copyText && <CopyBtn text={copyText} />}
        </div>
      </div>
      <div className="px-5 py-5">{children}</div>
    </div>
  );
}

// ── Export helpers ──

function toMarkdown(r: SalesPageData): string {
  const lines: string[] = [];
  lines.push(`# ${r.headline}`, `## ${r.subheadline}`, "");
  lines.push("## The Problem", ...r.painPoints.map(p => `- ${p}`), "");
  lines.push("## The Solution", r.solutionIntro, ...r.solutionBullets.map(b => `- ${b}`), "");
  lines.push("## What You Get", ...r.whatYouGet.map(w => `- **${w.item}**: ${w.description}`), "");
  lines.push("## Testimonials", ...r.testimonials.map(t => `> "${t.quote}" — ${t.name}, ${t.role}`), "");
  lines.push(`## Pricing`, `Price: ${r.pricing.price} (Value: ${r.pricing.totalValue})`, ...r.pricing.valueStack.map(v => `- ${v}`), "");
  lines.push(`CTA: ${r.ctaButtonText}`, "");
  if (r.guarantee) lines.push(`## Guarantee`, r.guarantee, "");
  lines.push("## FAQ", ...r.faqs.map(f => `**${f.question}**\n${f.answer}`), "");
  lines.push(`## Final CTA`, r.footerCta.closingStatement, `Button: ${r.footerCta.buttonText}`);
  return lines.join("\n");
}

function toPlainText(r: SalesPageData): string {
  return toMarkdown(r).replace(/[#*>`_\-]/g, "").replace(/\n{3,}/g, "\n\n");
}

function toHTML(r: SalesPageData): string {
  return `<!DOCTYPE html>
<html><head><meta charset="utf-8"><style>
body{font-family:system-ui,sans-serif;max-width:720px;margin:0 auto;padding:2rem;color:#1a1a1a}
h1{font-size:2.5rem;line-height:1.2}h2{font-size:1.5rem;margin-top:2rem;border-bottom:2px solid #eee;padding-bottom:.5rem}
ul{padding-left:1.5rem}li{margin-bottom:.5rem}
blockquote{border-left:3px solid #ccc;margin:1rem 0;padding:.5rem 1rem;font-style:italic}
.cta{display:inline-block;padding:1rem 2rem;background:#2563eb;color:#fff;border-radius:.5rem;font-weight:700;font-size:1.1rem;text-decoration:none;margin:1rem 0}
.price{font-size:2.5rem;font-weight:700;color:#2563eb;text-align:center;margin:1rem 0}
.anchor{text-decoration:line-through;color:#999;font-size:1.2rem}
</style></head><body>
<h1>${esc(r.headline)}</h1>
<h2>${esc(r.subheadline)}</h2>
<h2>The Problem</h2><ul>${r.painPoints.map(p => `<li>${esc(p)}</li>`).join("")}</ul>
<h2>The Solution</h2><p>${esc(r.solutionIntro)}</p><ul>${r.solutionBullets.map(b => `<li>${esc(b)}</li>`).join("")}</ul>
<h2>What You Get</h2><ul>${r.whatYouGet.map(w => `<li><strong>${esc(w.item)}</strong>: ${esc(w.description)}</li>`).join("")}</ul>
<h2>What Others Say</h2>${r.testimonials.map(t => `<blockquote>"${esc(t.quote)}"<br>— ${esc(t.name)}, ${esc(t.role)}</blockquote>`).join("")}
<h2>Pricing</h2><p class="anchor">${esc(r.pricing.anchor)}</p><p class="price">${esc(r.pricing.price)}</p>
<ul>${r.pricing.valueStack.map(v => `<li>${esc(v)}</li>`).join("")}</ul>
<p style="text-align:center"><a class="cta" href="#">${esc(r.ctaButtonText)}</a></p>
${r.guarantee ? `<h2>Guarantee</h2><p>${esc(r.guarantee)}</p>` : ""}
<h2>FAQ</h2>${r.faqs.map(f => `<p><strong>${esc(f.question)}</strong></p><p>${esc(f.answer)}</p>`).join("")}
<h2>${esc(r.footerCta.closingStatement)}</h2>
<p style="text-align:center"><a class="cta" href="#">${esc(r.footerCta.buttonText)}</a></p>
</body></html>`;
}

function esc(s: string) { return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;"); }

function downloadFile(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Main Component ──

interface Props {
  data: SalesPageData;
  onUpdate: (d: SalesPageData) => void;
  onRegenerateSection: (key: string) => Promise<void>;
}

export default function SalesPageResults({ data, onUpdate, onRegenerateSection }: Props) {
  const r = data;

  const updateField = (key: string, value: any) => {
    onUpdate({ ...r, [key]: value });
  };

  const markdown = toMarkdown(r);

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-xl font-display font-bold text-foreground flex items-center gap-2">
          <FileOutput className="w-5 h-5 text-primary" />
          Your Sales Page Blueprint
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          <CopyBtn text={toPlainText(r)} label="Plain Text" />
          <CopyBtn text={markdown} label="Markdown" />
          <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground" onClick={() => {
            navigator.clipboard.writeText(toHTML(r));
            toast.success("HTML copied!");
          }}>
            <Code className="w-3.5 h-3.5" /><span className="text-xs">Copy HTML</span>
          </Button>
          <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground" onClick={() => downloadFile(toHTML(r), "sales-page.html", "text/html")}>
            <Download className="w-3.5 h-3.5" /><span className="text-xs">Download HTML</span>
          </Button>
          <Button variant="ghost" size="sm" className="gap-1.5 h-8 px-2 text-muted-foreground hover:text-foreground" onClick={() => downloadFile(markdown, "sales-page.md", "text/markdown")}>
            <FileText className="w-3.5 h-3.5" /><span className="text-xs">Download MD</span>
          </Button>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">Click any text to edit inline. Use "Regenerate" to rewrite individual sections.</p>

      {/* Headline */}
      <SectionCard badge="Headline" badgeColor="bg-primary/20 text-primary" icon={Type} copyText={`${r.headline}\n${r.subheadline}`} sectionKey="headline" onRegenerate={onRegenerateSection}>
        <p className="text-2xl font-display font-bold text-foreground leading-tight">
          <EditableText value={r.headline} onSave={(v) => updateField("headline", v)} />
        </p>
        <p className="text-lg text-muted-foreground mt-2">
          <EditableText value={r.subheadline} onSave={(v) => updateField("subheadline", v)} />
        </p>
      </SectionCard>

      {/* Hero Visual */}
      <SectionCard badge="Hero Visual" badgeColor="bg-secondary/20 text-secondary" icon={Image} copyText={r.heroVisual} sectionKey="heroVisual" onRegenerate={onRegenerateSection}>
        <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/30 border border-border/30">
          <Image className="w-5 h-5 text-muted-foreground mt-0.5 shrink-0" />
          <p className="text-sm text-foreground">
            <EditableText value={r.heroVisual} onSave={(v) => updateField("heroVisual", v)} multiline />
          </p>
        </div>
      </SectionCard>

      {/* Pain Points */}
      <SectionCard badge="Problem Section" badgeColor="bg-destructive/20 text-destructive" icon={AlertCircle} copyText={r.painPoints.join("\n")} sectionKey="painPoints" onRegenerate={onRegenerateSection}>
        <div className="space-y-3">
          {r.painPoints.map((point, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center text-destructive text-xs font-bold shrink-0 mt-0.5">{i + 1}</span>
              <p className="text-foreground">
                <EditableText value={point} onSave={(v) => {
                  const arr = [...r.painPoints]; arr[i] = v; updateField("painPoints", arr);
                }} />
              </p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Solution */}
      <SectionCard badge="Solution Section" badgeColor="bg-primary/20 text-primary" icon={Zap} copyText={`${r.solutionIntro}\n\n${r.solutionBullets.join("\n")}`} sectionKey="solution" onRegenerate={onRegenerateSection}>
        <p className="text-foreground mb-4">
          <EditableText value={r.solutionIntro} onSave={(v) => updateField("solutionIntro", v)} multiline />
        </p>
        <div className="space-y-2">
          {r.solutionBullets.map((bullet, i) => (
            <div key={i} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-primary mt-1 shrink-0" />
              <p className="text-sm text-foreground">
                <EditableText value={bullet} onSave={(v) => {
                  const arr = [...r.solutionBullets]; arr[i] = v; updateField("solutionBullets", arr);
                }} />
              </p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* What You Get */}
      <SectionCard badge="What You Get" badgeColor="bg-primary/20 text-primary" icon={ListChecks} copyText={r.whatYouGet.map(w => `${w.item}: ${w.description}`).join("\n")} sectionKey="whatYouGet" onRegenerate={onRegenerateSection}>
        <div className="space-y-3">
          {r.whatYouGet.map((item, i) => (
            <div key={i} className="flex items-start gap-3 p-3 rounded-lg bg-muted/20 border border-border/20">
              <Package className="w-4 h-4 text-primary mt-1 shrink-0" />
              <div>
                <p className="font-semibold text-foreground text-sm">
                  <EditableText value={item.item} onSave={(v) => {
                    const arr = [...r.whatYouGet]; arr[i] = { ...arr[i], item: v }; updateField("whatYouGet", arr);
                  }} />
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  <EditableText value={item.description} onSave={(v) => {
                    const arr = [...r.whatYouGet]; arr[i] = { ...arr[i], description: v }; updateField("whatYouGet", arr);
                  }} />
                </p>
              </div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Social Proof */}
      <SectionCard badge="Social Proof" badgeColor="bg-secondary/20 text-secondary" icon={MessageSquareQuote} copyText={r.testimonials.map(t => `"${t.quote}" — ${t.name}, ${t.role}`).join("\n\n")} sectionKey="testimonials" onRegenerate={onRegenerateSection}>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 mb-4 text-xs text-amber-200/80">
          <AlertCircle className="w-3.5 h-3.5 text-amber-400 mt-0.5 shrink-0" />
          Replace these with real testimonials from your customers
        </div>
        <div className="space-y-4">
          {r.testimonials.map((t, i) => (
            <div key={i} className="p-4 rounded-lg bg-muted/20 border border-border/20">
              <p className="text-foreground italic mb-2">
                "<EditableText value={t.quote} onSave={(v) => {
                  const arr = [...r.testimonials]; arr[i] = { ...arr[i], quote: v }; updateField("testimonials", arr);
                }} />"
              </p>
              <p className="text-xs text-muted-foreground font-semibold">— {t.name}, {t.role}</p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Pricing */}
      <SectionCard badge="Pricing Section" badgeColor="bg-primary/20 text-primary" icon={DollarSign} copyText={`Price: ${r.pricing.price}\nValue: ${r.pricing.totalValue}\n\n${r.pricing.valueStack.join("\n")}`} sectionKey="pricing" onRegenerate={onRegenerateSection}>
        <div className="text-center p-6 rounded-xl bg-muted/20 border border-border/30">
          {r.pricing.anchor && <p className="text-muted-foreground line-through text-lg mb-1">{r.pricing.anchor}</p>}
          <p className="text-4xl font-display font-bold text-primary">{r.pricing.price}</p>
          <p className="text-xs text-muted-foreground mt-1">Total Value: {r.pricing.totalValue}</p>
          <div className="mt-4 space-y-1 text-left max-w-sm mx-auto">
            {r.pricing.valueStack.map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm">
                <Check className="w-3.5 h-3.5 text-primary shrink-0" />
                <span className="text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {/* CTA */}
      <SectionCard badge="CTA Button" badgeColor="bg-primary/20 text-primary" icon={MousePointerClick} copyText={r.ctaButtonText} sectionKey="ctaButtonText" onRegenerate={onRegenerateSection}>
        <div className="text-center">
          <div className="inline-block px-8 py-4 rounded-xl bg-primary text-primary-foreground font-bold text-lg">
            <EditableText value={r.ctaButtonText} onSave={(v) => updateField("ctaButtonText", v)} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">Use this exact text on your buy button</p>
        </div>
      </SectionCard>

      {/* Guarantee */}
      {r.guarantee && (
        <SectionCard badge="Guarantee" badgeColor="bg-secondary/20 text-secondary" icon={Shield} copyText={r.guarantee} sectionKey="guarantee" onRegenerate={onRegenerateSection}>
          <div className="flex items-start gap-3 p-4 rounded-lg bg-muted/20 border border-border/20">
            <Shield className="w-5 h-5 text-secondary mt-0.5 shrink-0" />
            <p className="text-foreground">
              <EditableText value={r.guarantee} onSave={(v) => updateField("guarantee", v)} multiline />
            </p>
          </div>
        </SectionCard>
      )}

      {/* FAQ */}
      <SectionCard badge="FAQ" badgeColor="bg-muted text-muted-foreground" icon={HelpCircle} copyText={r.faqs.map(f => `Q: ${f.question}\nA: ${f.answer}`).join("\n\n")} sectionKey="faqs" onRegenerate={onRegenerateSection}>
        <div className="space-y-4">
          {r.faqs.map((faq, i) => (
            <div key={i} className="border-b border-border/20 pb-3 last:border-0 last:pb-0">
              <p className="font-semibold text-foreground text-sm">
                <EditableText value={faq.question} onSave={(v) => {
                  const arr = [...r.faqs]; arr[i] = { ...arr[i], question: v }; updateField("faqs", arr);
                }} />
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <EditableText value={faq.answer} onSave={(v) => {
                  const arr = [...r.faqs]; arr[i] = { ...arr[i], answer: v }; updateField("faqs", arr);
                }} multiline />
              </p>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Footer CTA */}
      <SectionCard badge="Footer CTA" badgeColor="bg-primary/20 text-primary" icon={MousePointerClick} copyText={`${r.footerCta.closingStatement}\n\nButton: ${r.footerCta.buttonText}`} sectionKey="footerCta" onRegenerate={onRegenerateSection}>
        <p className="text-foreground text-lg font-medium mb-4">
          <EditableText value={r.footerCta.closingStatement} onSave={(v) => updateField("footerCta", { ...r.footerCta, closingStatement: v })} multiline />
        </p>
        <div className="text-center">
          <div className="inline-block px-8 py-4 rounded-xl bg-primary text-primary-foreground font-bold text-lg">
            <EditableText value={r.footerCta.buttonText} onSave={(v) => updateField("footerCta", { ...r.footerCta, buttonText: v })} />
          </div>
        </div>
      </SectionCard>

      <p className="text-xs text-muted-foreground text-center">Copy each section and paste into Gumroad, Beacons, Carrd, or your own website.</p>
    </div>
  );
}
